import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { env } from '@/config/env';
import { Role, useAuth } from '@/contexts/AuthContext';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n';
import { cn } from '@/lib/utils';

interface LanguagePopoverDevProps {
  position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export function LanguagePopover({ position }: LanguagePopoverDevProps) {
  const { user } = useAuth();

  const { i18n } = useTranslation('translation');

  if (env.PROD && user?.role.id !== Role.Root) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed z-50',
        position === 'top-right' && 'right-3 top-3',
        position === 'top-left' && 'left-3 top-3',
        position === 'bottom-right' && 'bottom-3 right-3',
        position === 'bottom-left' && 'bottom-3 left-3',
      )}
    >
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50"
          >
            <Globe className="h-5 w-5 text-gray-600" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-48 p-2" align="end">
          <div className="space-y-1">
            <p className="px-2 py-1 text-sm font-medium text-gray-900">
              Select language
            </p>

            {SUPPORTED_LANGUAGES.map((language) => (
              <button
                key={language.value}
                onClick={() => i18n.changeLanguage(language.value)}
                className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors hover:bg-gray-100 ${
                  i18n.language === language.value
                    ? 'bg-gray-100 font-medium'
                    : ''
                }`}
              >
                <span className="text-lg">{language.flag}</span>
                <span>{language.label}</span>

                {i18n.language === language.value && (
                  <div className="ml-auto h-2 w-2 rounded-full bg-black" />
                )}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
